import { Card, Col, Row } from 'react-bootstrap';
import { BsCameraVideo, BsClock, BsFiletypePdf } from 'react-icons/bs';
import { FaMapMarkerAlt } from 'react-icons/fa';
import LineDiciplineImg from '../../assets/images/line-of-diciplines.png';
import { GoButton } from '../../components/buttons/GoButton';
import { downloadInvoice } from '../../services/api';
import { Course } from '../../types/course.type';
import { Order } from '../../types/orders.type';
import { getCourseSessionTimeDisplay } from '../../utils/dateUtils';
import { formatTwoDates } from '../../utils/utils';
import AddFavorite from '../home/search/details/AddFavorite';

interface SessionCardProps {
  course: Course;
  edit: () => void;
  isFavoritePage?: boolean;
  order?: Order;
  updateFavorites?: () => void;
}

const SessionCard = ({
  order,
  course,
  edit,
  isFavoritePage,
  updateFavorites
}: SessionCardProps) => {
  const handlePreviewInvoicePDF = async (id: string | number) => {
    downloadInvoice({ path: `orders/generate-order/${id}` });
  };

  const isOnline = Boolean(course.is_online);
  const videoUrl = course.video_url?.trim();

  return (
    <Card className="mb-4 mt-4 text-left shadow">
      <Card.Body>
        {/* Row 1: Image - centered on mobile, left on desktop */}
        <Row className="mb-2">
          <Col xs={12} className="d-flex justify-content-center justify-content-md-start">
            <Card.Img
                src={LineDiciplineImg}
                style={{ height: '48px', width: '276px', maxWidth: '100%' }}
            />
          </Col>
        </Row>

        {/* Row 2: Order # left + View Receipt right */}
        {order && (
            <Row className="mb-3">
              <Col xs={12} className="d-flex align-items-center justify-content-between">
                <Card.Text
                    className="text-secondary mb-0"
                    style={{ fontWeight: 600 }}
                >
                  Order #{order.id}
                </Card.Text>
                <button
                    onClick={handlePreviewInvoicePDF.bind(this, order.id)}
                    className="btn btn-sm btn-primary fw-bolder text-uppercase"
                >
                  <BsFiletypePdf /> View Receipt
                </button>
              </Col>
            </Row>
        )}

        {isFavoritePage && (
            <Row className="mb-3">
              <Col xs={12} className="d-flex justify-content-end">
                <AddFavorite
                    show_label={false}
                    course_id={course.id}
                    updateFavorites={updateFavorites}
                />
              </Col>
            </Row>
        )}
        <Row>
          <Col sm={12}>
            <Card.Text className={'h2 text-capitalize text-info fw-bold'}>{course.title}</Card.Text>
          </Col>
        </Row>
        <Row className={'mb-3'}>
          <Col sm={12}>
            <Card.Text className={'h4 text-capitalize text-secondary fw-bold'}>
              {formatTwoDates(course.starts_on, course.ends_on)}
            </Card.Text>
          </Col>
        </Row>
        <Card.Subtitle className="text-muted">
          {!isOnline ? 'In-Person' : 'Online'}
        </Card.Subtitle>
        <Card.Text className="text-info fw-bold d-flex align-items-center">
          {isOnline ? (
            <>
              <BsCameraVideo className="me-2" />
              {videoUrl ? (
                <a
                  href={videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', color: '#6098B8' }}
                >
                  Join via Zoom
                </a>
              ) : (
                <span>Online</span>
              )}
            </>
          ) : (
            <>
              <FaMapMarkerAlt className="me-2" />
              {course.venue
                ? (course.venue.location.business_entity_name ?? course.venue.location.title)
                : ''}
            </>
          )}
        </Card.Text>
        <Card.Subtitle className="text-muted">Time</Card.Subtitle>
        <Card.Text className="d-flex align-items-center">
          <BsClock /> {getCourseSessionTimeDisplay(course) || course.formatted_time}
        </Card.Text>
        <Card.Subtitle>
          <GoButton
            text="View Full Session Details"
            url={`/sessions/${course.slug ?? course.id}`}
          />
        </Card.Subtitle>
        {!isFavoritePage && (
          <Card.Subtitle>
            <GoButton
              text="View Class Details & Recipes"
              url={`/user/class-details-recipes/${course.starts_on}`}
            />
          </Card.Subtitle>
        )}
      </Card.Body>
    </Card>
  );
};

export default SessionCard;
